module Widgets.Form.TestForm where

import Common

import Concur.React.DOM as D
import Concur.React.Props as P
import Data.Argonaut (stringify)
import Data.Argonaut as J
import Data.Date (Date, exactDate)
import Data.DateTime (DateTime(..), Time(..))
import Data.Enum (toEnum)
import Data.Maybe (fromJust)
import Effect.Class (liftEffect)
import Effect.Class.Console (log)
import Error (renderError_)
import GraphQL.Shape (shapeToString)
import GraphQL.Type (typedId)
import Partial.Unsafe (unsafePartial)
import Unsafe.Coerce (unsafeCoerce)
import Widgets.Form (FormState, formData, formShape, formWidget, setFormData)
import Widgets.Form.Access (fullFormAccess)
import Widgets.Form.Checkbox (Checkbox(..))
import Widgets.Form.Class (FormWidgetResult(..), blankFormContext)
import Widgets.Form.Date (DateInput(..), DateTimeInput(..))
import Widgets.Form.Dynamic (wrapFormWidget)
import Widgets.Form.EmailInput (EmailInput(..))
import Widgets.Form.EmployerSelect (initEmployerSelect)
import Widgets.Form.Expectations (courseExpectationsWidget, employerExpectationsWidget)
import Widgets.Form.List (simpleListWidget)
import Widgets.Form.NumberInput (NumberInput(..))
import Widgets.Form.PhoneInput (PhoneInput(..))
import Widgets.Form.Radio (Radio(..))
import Widgets.Form.Select (initSelectWidget)
import Widgets.Form.Simple (FormError(..), Label(..))
import Widgets.Form.StudentSubForm (StudentSubForm(..))
import Widgets.Form.SubForm (SubForm(..))
import Widgets.Form.TextInput (TextArea(..), TextInput(..))

testDate :: Date
testDate = unsafePartial $ fromJust $ do
    -- Should always be `Just` unless this code below is wrong.
    year <- toEnum 1984
    month <- toEnum 1
    day <- toEnum 1
    exactDate year month day

testDateTime :: DateTime
testDateTime = unsafePartial $ fromJust $ do
    -- Should always be `Just` unless this code below is wrong.
    hour <- toEnum 8
    minute <- toEnum 0
    second <- toEnum 0
    ms <- toEnum 0
    let time = Time hour minute second ms
    pure $ DateTime testDate time


testForm :: FormState
testForm =
    { id: typedId "test"
    , label: "Test"
    , access: fullFormAccess
    , fields:
        [ wrapFormWidget $ Label
            { id: "header"
            , label: "Test:"
            , cls: "text-lg"
            }
        , wrapFormWidget $ initEmployerSelect
            { id: "employer-select"
            , cls: ""
            , label: "Employer select"
            , contact: true
            }
        , wrapFormWidget $ StudentSubForm $ SubForm
            { id: "student-subform"
            , label: Just "<b>Student Information</b>"
            , labelClass: ""
            , cls: ""
            , section: true, collapsible: true, expanded: true
            , fields:
                [ wrapFormWidget $ TextInput
                    { id: "name"
                    , label: "Student Name"
                    , cls: ""
                    , placeholder: Nothing
                    , value: "" }
                ]
            }
        , wrapFormWidget $ Label
            { id: "misc"
            , label: "<h1>Misc</h1>"
            , cls: ""
            }
        , wrapFormWidget $ TextInput
            { id: "text"
            , label: "<b>Test textbox</b>"
            , cls: ""
            , placeholder: Just "Placeholder Test"
            , value: "foo" }
        , wrapFormWidget $ TextInput
            { id: "empty-text"
            , label: "Empty textbox"
            , cls: ""
            , placeholder: Just "Empty"
            , value: "" }
        , wrapFormWidget $ courseExpectationsWidget
            (SubForm
                { id: "subform"
                , label: Just "Collapsible sub-form test"
                , labelClass: ""
                , section: true
                , collapsible: true
                , expanded: true
                , cls: ""
                , fields:
                    [ wrapFormWidget $ TextArea
                        { id: "expectation"
                        , label: "Expectation"
                        , cls: ""
                        , placeholder: Just "Placeholder Test"
                        , value: "" }
                    , wrapFormWidget $ TextArea
                        { id: "learning"
                        , label: "Learning"
                        , cls: ""
                        , placeholder: Just "Placeholder Test"
                        , value: "" }
                    ]
                })
            { id: "course-expectations"
            , cls: ""
            , label: "Course Expectations" }
        , wrapFormWidget $ employerExpectationsWidget
            (SubForm
                { id: "subform"
                , label: Just "Collapsible sub-form test"
                , labelClass: ""
                , section: true
                , collapsible: true
                , expanded: true
                , cls: ""
                , fields:
                    [ wrapFormWidget $ TextArea
                        { id: "expectation"
                        , label: "Expectation"
                        , cls: ""
                        , placeholder: Just "Placeholder Test"
                        , value: "" }
                    , wrapFormWidget $ TextArea
                        { id: "learning"
                        , label: "Learning"
                        , cls: ""
                        , placeholder: Just "Placeholder Test"
                        , value: "" }
                    ]
                })
            { id: "employer-expectations"
            , cls: ""
            , label: "Employer Expectations" }
        , wrapFormWidget $ simpleListWidget
            { id: "list"
            , label: "List of Textboxes"
            , cls: ""
            , template: wrapFormWidget $ TextInput
                { id: "list-text"
                , label: "Empty textbox"
                , cls: ""
                , placeholder: Just "Empty"
                , value: "" }
            }
        , wrapFormWidget $ TextArea
            { id: "textarea"
            , label: "Test text area"
            , cls: ""
            , placeholder: Just "Test"
            , value: "testing" }
        , wrapFormWidget $ PhoneInput
            { id: "phone"
            , label: "Test phone input"
            , cls: ""
            , placeholder: Just "Phone number"
            , value: "555-555-5555" }
        , wrapFormWidget $ EmailInput
            { id: "email"
            , label: "Test email input"
            , cls: ""
            , placeholder: Just "Email"
            , value: "email@example.com" }
        , wrapFormWidget $ NumberInput
            { id: "int"
            , label: "<b>Test int input</b>"
            , cls: ""
            , placeholder: Just "Integer"
            , step: 1.0
            , value: Just 42.0 }
        , wrapFormWidget $ NumberInput
            { id: "nint"
            , label: "Null int input"
            , cls: ""
            , placeholder: Just "Integer"
            , step: 1.0
            , value: Nothing }
        , wrapFormWidget $ NumberInput
            { id: "float"
            , label: "Test float input"
            , cls: ""
            , placeholder: Just "Decimal"
            , step: 0.1
            , value: Just 3.14 }
        , wrapFormWidget $ DateInput
            { id: "date"
            , label: "Test date input"
            , cls: ""
            , value: Just testDate }
        , wrapFormWidget $ DateInput
            { id: "null-date"
            , label: "Test date input"
            , cls: ""
            , value: Nothing }
        , wrapFormWidget $ DateTimeInput
            { id: "datetime"
            , label: "Test datetime input"
            , cls: ""
            , value: Nothing }
        , wrapFormWidget $ DateTimeInput
            { id: "test_datetime"
            , label: "Test datetime input"
            , cls: ""
            , value: Nothing }
        , wrapFormWidget $ Checkbox
            { id: "check"
            , label: "<b>Test checkbox</b>"
            , cls: ""
            , legacy: false
            , value: false }
        , wrapFormWidget $ Radio
            { id: "radio"
            , label: "Test radio"
            , cls: ""
            , options: [{value: "1", label: "One"}, {value: "2", label: "Two"}]
            , value: Just "1"
            }
        , wrapFormWidget $ Radio
            { id: "radio-null"
            , label: "Test radio with nothing selected"
            , cls: ""
            , options: [{value: "1", label: "One"}, {value: "2", label: "Two"}]
            , value: Nothing
            }
        , wrapFormWidget $ initSelectWidget
            { id: "select"
            , label: Just "Test select"
            , cls: ""
            , placeholder: Just "Placeholder"
            , options:
                [ {value: J.fromString "1", label: "Testing a very long label my goodness this is huge totally massive and worst of all no punctuation where we are going we dont need punctuation"}
                , {value: J.fromString "2", label: "Two"}
                ]
            , value: Just $ J.fromString "1"
            }
        , wrapFormWidget $ Label
            { id: "label"
            , label: "This is a label."
            , cls: ""
            }
        , wrapFormWidget $ Label
            { id: "label-html"
            , label: "This is a label <marquee>with HTML</marquee>."
            , cls: ""
            }
        , wrapFormWidget $ Label
            { id: "label-brokenhtml"
            , label: "This HTML is <b>missing a closing tag"
            , cls: ""
            }
        , wrapFormWidget $ FormError
            { id: "test-error"
            , err: StringError "This is a test of the erorr system."
            }
        , wrapFormWidget $ SubForm
            { id: "subform"
            , label: Just "Collapsible sub-form test"
            , labelClass: ""
            , section: true
            , collapsible: true
            , expanded: true
            , cls: ""
            , fields:
                [ wrapFormWidget $ TextInput
                    { id: "subtext"
                    , label: "Test textbox"
                    , cls: ""
                    , placeholder: Just "Placeholder Test"
                    , value: "" }
                , wrapFormWidget $ Checkbox
                    { id: "subcheck"
                    , label: "Test checkbox"
                    , cls: ""
                    , legacy: false
                    , value: false }
                ]
            }
        , wrapFormWidget $ SubForm
            { id: "nocollapse-subform"
            , label: Just "Non-collapsible sub-form test"
            , labelClass: ""
            , section: true
            , collapsible: false
            , expanded: true
            , cls: ""
            , fields:
                [ wrapFormWidget $ TextInput
                    { id: "subtext"
                    , label: "Test textbox"
                    , cls: ""
                    , placeholder: Just "Placeholder Test"
                    , value: "" }
                , wrapFormWidget $ Checkbox
                    { id: "subcheck"
                    , label: "Test checkbox"
                    , cls: ""
                    , legacy: false
                    , value: false }
                ]
            }
        , wrapFormWidget $ SubForm
            { id: "nolabel-subform"
            , label: Nothing
            , labelClass: ""
            , section: true
            , collapsible: false
            , expanded: true
            , cls: ""
            , fields:
                [ wrapFormWidget $ TextInput
                    { id: "subtext"
                    , label: "Test textbox in subform with no label"
                    , cls: ""
                    , placeholder: Just "Placeholder Test"
                    , value: "" }
                ]
            }
        , wrapFormWidget $ simpleListWidget
            { id: "subform-list"
            , label: "List of SubForms"
            , cls: ""
            , template: wrapFormWidget $ SubForm
                { id: "list-subform"
                , label: Just "List subform test"
                , labelClass: ""
                , section: true
                , collapsible: true
                , expanded: false
                , cls: ""
                , fields:
                    [ wrapFormWidget $ TextInput
                        { id: "subtext"
                        , label: "Test textbox"
                        , cls: ""
                        , placeholder: Just "Placeholder Test"
                        , value: "" }
                    , wrapFormWidget $ Checkbox
                        { id: "subcheck"
                        , label: "Test checkbox"
                        , cls: ""
                        , legacy: false
                        , value: false }
                    ]
                }
            }
        ]
    }

type TestFormState = { formState :: FormState }
data TestFormAction = Next (FormWidgetResult FormState) | Reset | ShowShape | TestLoad

renderTestForm :: forall a. Widget HTML a
renderTestForm =
    run { formState: testForm }
  where
    fc = blankFormContext
    initData = formData testForm
    initForm = testForm

    run :: TestFormState -> Widget HTML a
    run tfstate@{ formState: state } = do
        liftEffect $ log $ stringify $ formData state
        handle tfstate =<< D.div'
            [ D.button [P.className "btn", Reset <$ P.onClick] [D.text "Reset"]
            , D.button [P.className "btn", ShowShape <$ P.onClick] [D.text "Print Query Shape"]
            , D.button [P.className "btn", TestLoad <$ P.onClick] [D.text "Simulate Load"]
            , Next <$> formWidget fc state
            ]

    handle state (Next (FormWidgetResult { state: next, action: _ })) = run $ state { formState = next }
    handle state ShowShape = do
        liftEffect $ log $ shapeToString $ formShape $ state.formState
        run state
    handle state Reset =
        case setFormData blankFormContext initData state.formState of
             Right next -> run $ state { formState = next }
             Left err -> renderError_ err
    handle state TestLoad = do
        -- Load the current form state's data into the initial form state
        let fdata = formData state.formState
        liftEffect $ log $ unsafeCoerce fdata
        case setFormData blankFormContext fdata initForm of
            Right formState -> run state { formState = formState }
            Left err -> renderError_ err
